import { deepFreeze } from "@/utils";

/**
 * Gip auth error codes
 */
export const AUTH_GIP_ERROR_CODES = {
  userNotFound: "auth/user-not-found",
  wrongPassword: "auth/wrong-password",
  accountNotFound: "auth/account-not-found",
  mfaRequired: "auth/multi-factor-auth-required",
  accountAlreadyExists: "auth/account-exists-with-different-credential",
  providerAlreadyLinked: "auth/provider-already-linked",
  weakPassword: "auth/weak-password",
  accountDisabled: "auth/user-disabled",
  invalidUserActionCode: "auth/invalid-user-action-code",
};
/**
 * Firebase user details property to be fetched from firebase user
 * object
 */
export const FIREBASE_USER_DETAILS = Object.freeze([
  "email",
  "uid",
  "firstName",
  "lastName",
  "companyName",
  "phoneNumber",
  "photoURL",
  "displayName",
  "emailVerified",
]);

/**
 * Error messages
 */
export const ERROR_MESSAGES = Object.freeze({
  enableMfa: "Enable MFA to access Marketing Cloud.",
  mfaError: "User requires 2FA",
  userDoesNotExists: "User doesn’t exist on {{domain_name}}",
  default: "Something went wrong",
});

/**
 * Gip auth error codes and their error messages
 */
export const AUTH_ERROR_CASE_MESSAGES = Object.freeze({
  "auth/requires-recent-login": "Please login again.",
  "auth/too-many-requests":
    "All requests from this device are blocked. Try again later..",
  "auth/invalid-phone-number": "Invalid phone number.",
  "auth/invalid-verification-code":
    "The 6-digit verification code you provided is invalid.",
  "auth/user-not-found": "Invalid Email or password.",
  "auth/second-factor-already-in-use":
    "Phone Number already enrolled as MFA for this account.",
  "auth/invalid-password": "Invalid Email or password.",
  "auth/wrong-password": "Invalid Email or password.",
  "auth/account-not-found": "Invalid Email or password.",
  EMAIL_NOT_FOUND:
    "If you have a valid account, password recovery instructions have been sent to the registered email address.",
  "auth/invalid-totp-code":
    "The 6-digit verification code you provided is invalid.",
  socialUserNotFound: `To activate this connection please first sign in with your Maropost credentials, or another connected third-party.`,
  "auth/invalid-action-code":
    "The link you are trying to access is invalid. Please contact system support.",
  "auth/invalid-user-action-code":
    "The link you are trying to access is invalid. Please contact system support.",
  "auth/expired-action-code":
    "The link you are trying to access has expired. Please contact system support.",
  "auth/user-disabled":
    "Your account is currently inactive. Please contact system support",
  "auth/user-cancelled": "Login failed! Request cancelled by the user.",
  "auth/maropost-user-not-found": "User doesn’t exist on {{domain_name}}",
  "auth/maropost-account-not-found": `User doesn’t have access to any account on {{domain_name}}`,
});

/**
 * Display breakpoints
 */
export const DISPLAY_BREAKPOINTS = {
  xs: "xs",
  sm: "sm",
  md: "md",
  lg: "lg",
  xl: "xl",
};

/**
 * Common api respose codes
 */
export const RESPONSE_CODES = {
  success: 200,
  badRequest: 400,
  unAuthorised: 401,
  notFound: 404,
  internalServerError: 500,
  created: 201,
  noContent: 204,
  forbidden: 403,
  unProcessed: 422,
};

/**
 * Invalid maropost product domains to be ignored as a refferer
 * @type {Object}
 */
export const INVALID_MAROPOST_REFFERERS = {
  galaxy: "https://galaxy.maropost.com",
  maropost: "https://www.maropost.com",
};

/**
 * Maropost saml id to be used for Galaxy login
 */
export const MAROPOST_SAML_ID = "maropost_saml_id";

/**
 * Marketing clusters list
 */
export const MARKETING_CLUSTERS = Object.freeze([
  {
    name: "App Login",
    link: "https://app.maropost.com",
    element_id: "app-cluster-login-link",
  },
  {
    name: "Cloud Login",
    link: "https://cloud.maropost.com",
    element_id: "cloud-cluster-login-link",
  },
  {
    name: "Cloud1 Login",
    link: "https://cloud1.maropost.com",
    element_id: "cloud1-cluster-login-link",
  },
  {
    name: "EU1 Login",
    link: "https://app-eu1.maropost.com",
    element_id: "eu-cluster-login-link",
  },
  {
    name: "CA1 Login",
    link: "https://app-ca1.maropost.com",
    element_id: "ca-cluster-login-link",
  },
  {
    name: "Uat Login",
    link: "https://uat.maropost.com",
    element_id: "uat-cluster-login-link",
  },
  {
    name: "App Uat Login",
    link: "https://app-uat.maropost.com",
    element_id: "app-uat-cluster-login-link",
  },
]);

/**
 * Mfa options in Maropost Identity platform
 */
export const MFA_OPTIONS = deepFreeze([
  {
    name: "Text Message",
    value: "textMessage",
    id: "phone",
    identifier: "mfa-text-message",
  },
  {
    id: "totp",
    name: "Security App",
    value: "totp",
    identifier: "mfa-authenticator-app",
  },
]);

/**
 * Salesfire galaxy domain name
 */
export const GALAXY_DOMAIN = "https://galaxy.maropost.com";

/**
 * USER_PROPS
 * User profile detail properties
 */
export const USER_PROPS = [
  "firstName",
  "lastName",
  "companyName",
  "phoneNumber",
  "mfaProvider",
  "totpSecret",
];

export const RESEND_VERIFICATION_CODE_MILLIS = 1500_0;

/**
 * RECAPTCHA_TYPES
 * Types of recaptcha
 */
export const RECAPTCHA_TYPES = deepFreeze({
  normal: "normal",
  invisible: "invisible",
});

/**
 * AUTH_MESSAGES
 * Auth messages to be shown to the user
 */
export const AUTH_MESSAGES = {
  codeReattemptsExhausted:
    "Maximum re-attempts has been reached. Please try again after 5 minutes.",
  codeResentSuccessfully: "Code has been re-sent successfully.",
};

/**
 * MAROPOST_LOCAL_DOMAIN
 * Local dev maropost domain
 */
export const MAROPOST_LOCAL_DOMAIN = "http://app.maropost.local:8080";

/**
 * EMAIL_ACTION_CODES
 * Email action codes
 */
export const EMAIL_ACTION_CODES = {
  verifyEmail: "verifyEmail",
  recoverEmail: "recoverEmail",
  resetPassword: "resetPassword",
  revertSecondFactorAddition: "revertSecondFactorAddition",
};

export const IDENTITY_AUTH_ERROR_CODES = {
  userNotFound: "auth/maropost-user-not-found",
  accountNotFound: "auth/maropost-account-not-found",
};

/**
 * App enviroments enum (Immutable)
 */
export const ENVS = deepFreeze({
  development: "development",
  test: "test",
  production: "production",
  staging: "staging",
});
